import {
  initialQueryKeyInfo,
  useKeyTable,
} from "hook/subscription/useKeyTable";
import { useEffect, useMemo, useState } from "react";
import { differenceInDays, getUtcDate } from "utils/timeUtil";

interface AddChannelInput {
  registeredChannel: number;
  expirationDate: Date | string;
  addChannels: number;
}

type Props = {
  registeredChannel: number;
  expiration: string;
  totalUnit?: number;
};

type CalculatedResult =
  | {
      unitDays: number;
      newNumberOfCameras: number;
      newExpirationDate: string;
      isCapped: boolean;
      maxExpireDate: string | null;
      error: null;
    }
  | {
      unitDays: number;
      newNumberOfCameras: null;
      newExpirationDate: null;
      isCapped: false;
      maxExpireDate: string | null;
      error: string;
    };

const toFiniteNumber = (value: unknown) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};
const oneDayInMs = 24 * 60 * 60 * 1000;

export function useAddChannelCalculator(props: Props) {
  const [inputs, setInputs] = useState<AddChannelInput>({
    registeredChannel: props.registeredChannel,
    expirationDate: getUtcDate(props.expiration).toUTCString(),
    addChannels: 0,
  });

  const { registeredChannel, expirationDate, addChannels } = inputs;

  const { subscriptionKeyList } = useKeyTable({
    queryInfo: {
      ...initialQueryKeyInfo,
      pageLimit: 100,
    },
    setQueryInfo: () => undefined,
  });

  const maxExpireDate = useMemo(() => {
    const validDates = subscriptionKeyList
      .filter(
        (key): key is typeof key & { expireDate: string } =>
          !key.dwKey.startsWith("3") && !!key.expireDate
      )
      .map((key) => key.expireDate);

    if (validDates.length === 0) return null;

    return validDates.reduce((max, curr) =>
      getUtcDate(curr).getTime() > getUtcDate(max).getTime() ? curr : max
    );
  }, [subscriptionKeyList]);

  const onChangeAddChannel = ({
    name,
    value,
  }: {
    name: string;
    value: any;
  }) => {
    setInputs((inp) => {
      return {
        ...inp,
        [name]: value,
      };
    });
  };

  const currentCalculatedResult = useMemo((): CalculatedResult | null => {
    const safeActivatedChannels = toFiniteNumber(registeredChannel);
    const safeAddChannels = toFiniteNumber(addChannels);
    const newNumberOfCameras = safeActivatedChannels + safeAddChannels;

    if (safeActivatedChannels <= 0) {
      return null;
    }

    const today = getUtcDate(new Date());
    const daysUntilExpiration = differenceInDays(expirationDate, today);

    if (daysUntilExpiration < 0) {
      return null;
    }

    if (newNumberOfCameras <= 0) {
      return {
        unitDays: 0,
        newNumberOfCameras: null,
        newExpirationDate: null,
        isCapped: false,
        maxExpireDate,
        error: "New channel count must be greater than 0",
      };
    }
    const currentTotalUnitDays = daysUntilExpiration * safeActivatedChannels;
    const calculatedUnitDays = Math.floor(
      currentTotalUnitDays / newNumberOfCameras
    );
    const calculatedExpirationDate = getUtcDate(
      new Date(today.getTime() + calculatedUnitDays * oneDayInMs)
    );

    const capDate = maxExpireDate ? getUtcDate(maxExpireDate) : null;
    const isCapped = Boolean(
      capDate && calculatedExpirationDate.getTime() > capDate.getTime()
    );

    const finalExpirationDate = isCapped ? capDate! : calculatedExpirationDate;
    const unitDays = differenceInDays(finalExpirationDate, today);

    return {
      unitDays,
      newNumberOfCameras,
      newExpirationDate: finalExpirationDate.toISOString(),
      isCapped,
      maxExpireDate,
      error: null,
    };
  }, [
    registeredChannel,
    addChannels,
    expirationDate,
    maxExpireDate,
    props.totalUnit,
  ]);

  const currentCalculatedResultNullMessage = useMemo(() => {
    const safeActivatedChannels = toFiniteNumber(registeredChannel);
    if (safeActivatedChannels <= 0) {
      return "No Registered Channels";
    }

    const today = getUtcDate(new Date());
    const daysUntilExpiration = differenceInDays(expirationDate, today);

    if (daysUntilExpiration < 0) {
      return "Subscription is expired";
    }
    return null;
  }, [registeredChannel, expirationDate]);

  return {
    inputs,
    onChangeAddChannel,
    currentCalculatedResult,
    currentCalculatedResultNullMessage,
  };
}
